import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  LogIn,
  Loader2,
  Key,
  Sun,
  Sparkles,
  Shield,
  Zap,
  ArrowRight,
} from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import appSession from "../../utils/appSession";

const Login = () => {
  const [credentials, setCredentials] = useState({
    identifier: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/login`,
        credentials,
      );
      const { token, isAdmin } = response.data;

      appSession.setPrimaryAuthToken(token);
      toast.success("Welcome to your smartitbox-powered dashboard!");
      if (isAdmin) {
        navigate("/admin");
      } else {
        navigate("/whatsappdashboard");
      }
    } catch (error) {
      toast.error("Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-1">
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email/Username */}
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4 text-amber-500" />
            Email or Username
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-500 group-hover:text-amber-500 transition-colors" />
            </div>
            <input
              type="text"
              name="identifier"
              value={credentials.identifier}
              onChange={handleChange}
              placeholder="Enter your email or username"
              className="w-full pl-12 pr-4 py-3 border border-amber-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-300 transition-all duration-300 bg-white/50 text-gray-900 placeholder-gray-500 hover:bg-white/80 hover:border-amber-300"
              required
            />
            {/* Sun rays effect on focus */}
            <div className="absolute inset-0 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-100/20 to-transparent blur-sm" />
            </div>
          </div>
        </div>

        {/* Password */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-500" />
              Password
            </label>
            <Link
              to="/forget-password"
              className="text-sm text-amber-600 hover:text-orange-600 font-medium transition-colors duration-200 flex items-center gap-1 group"
            >
              <Key className="w-3 h-3" />
              Forgot password?
            </Link>
          </div>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-500 group-hover:text-amber-500 transition-colors" />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={credentials.password}
              onChange={handleChange}
              placeholder="Enter your password"
              className="w-full pl-12 pr-12 py-3 border border-amber-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-300 transition-all duration-300 bg-white/50 text-gray-900 placeholder-gray-500 hover:bg-white/80 hover:border-amber-300"
              required
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-4 flex items-center group"
              onClick={() => setShowPassword(!showPassword)}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-amber-400 group-hover:text-amber-500 transition-colors" />
              ) : (
                <Eye className="h-5 w-5 text-amber-400 group-hover:text-amber-500 transition-colors" />
              )}
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-8 right-0 bg-gray-900 text-white text-xs py-1 px-2 rounded-lg whitespace-nowrap"
                >
                  {showPassword ? "Hide password" : "Show password"}
                </motion.div>
              )}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={loading}
          className="relative w-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white py-3.5 rounded-xl hover:from-amber-600 hover:via-orange-600 hover:to-amber-600 transition-all duration-300 flex items-center justify-center font-semibold shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed group overflow-hidden"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Content */}
          <div className="relative flex items-center justify-center gap-3">
            {loading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <Loader2 className="w-5 h-5" />
                </motion.div>
                <span>Harnessing Power...</span>
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                <span>Access Dashboard</span>
              </>
            )}
          </div>
        </motion.button>
      </form>
    </div>
  );
};

export default Login;
